import React, { useState } from "react";
import { generateInspectionPDF } from "./InspectionPDFReport";
import { generateAuditPDF } from "./AuditPDFReport";
import "pdfjs-dist/legacy/build/pdf.worker.entry";
import "../../.csscode/ExportPrint.css";
import GenericModal from "../GenericModal";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

const ExportPrint = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // "I" = Inspection report (default), "A" = Audit report
  const mode = props.mode || "I";

  const handleSelect = (type) => {
    setModalOpen(false);
    handlePrint(type);
  };

  const handlePrint = async (type) => {
    let newTab = null;
    let interval = null;

    try {
      setIsLoading(true);
      const propsDetails = props.details || [];
      const allAnnexures = props.annextureList || [];

      // Step 1: Filter used annexure IDs
      const usedAnnexureIds = new Set(
        propsDetails.filter((i) => i.annexure).map((i) => i.annexure),
      );
      const annextureList = allAnnexures.filter((a) =>
        usedAnnexureIds.has(a.value),
      );

      // 1️⃣ Open New Tab
      newTab = window.open("", "_blank");
      if (!newTab) {
        console.error("Popup blocked. Please allow popups for this site.");
        return;
      }

      newTab.document.write(`
  <html>
  <head>
    <title>Preparing Report...</title>
    <style>
      body {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100vh;
        margin: 0;
        background: #eef1f6;
        font-family: Arial, sans-serif;
      }

      .loader-card {
        background: #ffffff;
        padding: 35px 45px;
        border-radius: 14px;
        box-shadow: 0 6px 18px rgba(0,0,0,0.12);
        text-align: center;
        width: 390px;
        max-width: 90vw;
        box-sizing: border-box;
      }

      h2 {
        color: #333;
        margin: 0;
        font-size: 22px;
        font-weight: 600;
      }

      /* Progress Bar Container */
      .progress-bar-container {
        width: 100%;
        height: 24px;
        background: #d9dce1;
        border-radius: 25px;
        overflow: hidden;
        margin-top: 22px;
        position: relative;
      }

      /* Animated Stripes Effect */
      .progress-bar-fill {
        height: 100%;
        width: 0%;
        background: repeating-linear-gradient(
          45deg,
          #3f51b5,
          #3f51b5 10px,
          #5c6bc0 10px,
          #5c6bc0 20px
        );
        background-size: 40px 40px;
        animation: moveStripes 0.6s linear infinite;
        transition: width 0.25s ease;
      }

      @keyframes moveStripes {
        from { background-position: 0 0; }
        to { background-position: 40px 0; }
      }

      .progress-text {
        margin-top: 12px;
        font-size: 18px;
        font-weight: bold;
        color: #333;
      }
    </style>
  </head>

  <body>
    <div class="loader-card">
      <h2>Generating your report…</h2>

      <div class="progress-bar-container">
        <div id="progressFill" class="progress-bar-fill"></div>
      </div>

      <div id="progressText" class="progress-text">0%</div>
    </div>
  </body>
  </html>
`);

      newTab.document.close();

      let progress = 0;
      const progressFill = newTab.document.getElementById("progressFill");
      const progressText = newTab.document.getElementById("progressText");

      interval = setInterval(() => {
        if (progress < 92) {
          progress += Math.floor(Math.random() * 4) + 1;
          if (progress > 92) progress = 92;

          progressFill.style.width = progress + "%";
          progressText.innerHTML = progress + "%";
        }
      }, 250);

      // 2️⃣ Same parameters for both generators
      const reportParams = {
        isQuestion: type !== "without", // true = with questions, false = without
        orgData: props.orgdetails,
        auddetails: props.auddetails,
        observationdate: props.observationdate,
        user: props.user,
        empData: props.empData,
        divCdList: props.divcodeList,
        emptypeOptions: props.emptypeOptions,
        ackContent: props.ackContent,
        exSummaryContent: props.exSummaryContent,
        introductionContent: props.introductionContent,
        firstNote: props.firstNote,
        PriorityContent: props.PriorityContent,
        closingNotes: props.lastNote,
        recommendation: props.recommendation,
        conclusion: props.conclusion,
        references: props.references,
        abbreviation: props.abbreviation,
        details: propsDetails,
        scheduleid: props.scheduleid,
        annextureList: annextureList,
        documents: props.documents,
        auditeecode: props.auditeeCode,
      };

      // 3️⃣ Generate PDF: mode "A" → audit report, otherwise inspection report
      const blob =
        mode === "A"
          ? await generateAuditPDF(reportParams)
          : await generateInspectionPDF(reportParams);

      clearInterval(interval);
      interval = null;
      progressFill.style.width = "100%";
      progressText.innerHTML = "100%";

      // 4️⃣ Show PDF
      setTimeout(() => {
        if (blob && newTab) {
          const blobUrl = URL.createObjectURL(blob);
          newTab.location.href = blobUrl;
        }
      }, 500);
    } catch (error) {
      console.error("Error generating report:", error);
      // Don't leave the loader tab stuck at 92%
      if (newTab && !newTab.closed) newTab.close();
    } finally {
      if (interval) clearInterval(interval);
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="print-btn"
        disabled={
          props.noSchedule || props.isBodyLoading || props.loading || isLoading
        }
        onClick={() => setModalOpen(true)}
        title="Print Report"
      >
        <PrintOutlinedIcon />
        {isLoading ? "Preparing..." : "Print Report"}
      </button>

      <GenericModal
        open={modalOpen}
        title="Choose Report Type"
        message={
          mode === "A"
            ? "Do you want the Audit Report with questions or without questions?"
            : "Do you want the Inspection Report with questions or without questions?"
        }
        onSelect={handleSelect}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

export default ExportPrint;
